import React, { useState, useMemo } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import PageTitle from '@/components/PageTitle'
import OrgTree, { buildTree } from '@/components/OrgNodes/OrgTree'
import { Card } from '@/components/ui/Card'
import { TimeZoneOption } from '@/components/OrgNodes/CountryFields'
import NodeEditPanel, { OrgNodeData } from '@/components/OrgNodes/NodeEditPanel'
import { move_org_node_path } from '@/lib/routes'

interface Props {
  nodes: OrgNodeData[]
  sales_unflagged_node_ids: string[]
  currencies: string[]
  time_zones: TimeZoneOption[]
  can: { manage: boolean }
}

export default function OrgNodesIndex({ nodes: initialNodes, sales_unflagged_node_ids, currencies, time_zones, can }: Props) {
  const [nodes, setNodes] = useState<OrgNodeData[]>(initialNodes)
  const salesUnflaggedIds = useMemo(() => new Set(sales_unflagged_node_ids), [sales_unflagged_node_ids])
  const [panelMode, setPanelMode] = useState<'edit' | 'add-child' | null>(null)
  const [selectedNode, setSelectedNode] = useState<OrgNodeData | null>(null)
  const [parentNode, setParentNode] = useState<OrgNodeData | null>(null)

  const roots = useMemo(() => buildTree(nodes), [nodes])

  function openAddChild(parent: OrgNodeData) {
    setSelectedNode(null)
    setParentNode(parent)
    setPanelMode('add-child')
  }

  function openEdit(node: OrgNodeData) {
    setSelectedNode(node)
    setParentNode(null)
    setPanelMode('edit')
  }

  function handleSaved(savedNode: OrgNodeData) {
    setNodes((prev) => {
      const idx = prev.findIndex((n) => n.id === savedNode.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = savedNode
        return next
      }
      return [...prev, savedNode]
    })
  }

  function handleDeleted(id: string) {
    setNodes((prev) => prev.filter((n) => n.id !== id))
    closePanel()
  }

  function closePanel() {
    setPanelMode(null)
    setSelectedNode(null)
    setParentNode(null)
  }

  function handleMove(node: OrgNodeData, direction: 'left' | 'right') {
    fetch(move_org_node_path(node.id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ direction }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json()
          setNodes(data.nodes)
        }
      })
      .catch(() => { /* leave order unchanged on network error */ })
  }

  return (
    <AppLayout header={
      <PageTitle title="Org Structure" subtitle="Define your company hierarchy by region, channel, or team" />
    }>
      <div className="flex">
        <div className="flex-1">
          <div className="mb-5 p-4 bg-accent border border-border rounded-lg">
            <h2 className="text-sm font-semibold text-primary mb-1">About Org Structure</h2>
            <p className="text-sm text-primary leading-relaxed">
              Define your organization as a hierarchy. The root node represents the company as a
              whole, and child nodes break it down by region, channel, or team (e.g. Vietnam → Marketplace → Shopee).
            </p>
            <p className="text-sm text-primary leading-relaxed mt-2">
              Plans, targets, and reports are attached to nodes, so results can be tracked at any
              level and rolled up to the parent.
            </p>
            {can.manage && (
              <p className="text-sm text-primary leading-relaxed mt-2">
                Click a node to rename it, or use the <span className="font-medium">+</span> button
                to add a child. The root node cannot be deleted or reparented.
              </p>
            )}
          </div>
          <Card className="p-4">
            <OrgTree
              roots={roots}
              canManage={can.manage}
              salesUnflaggedIds={salesUnflaggedIds}
              onAddChild={openAddChild}
              onEdit={openEdit}
              onMove={handleMove}
            />
          </Card>
        </div>
        {panelMode && (
          <NodeEditPanel
            node={selectedNode}
            parentNode={parentNode}
            mode={panelMode}
            currencies={currencies}
            timeZones={time_zones}
            onClose={closePanel}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
          />
        )}
      </div>
    </AppLayout>
  )
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}
